import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import type { PredictionResponse } from "@/services/predictionService";

interface Props {
  prediction: PredictionResponse;
  envData?: any;
}

// Compute drivers based on projected change (predicted - current) normalized as percent-change
const PredictionDrivers: React.FC<Props> = ({ prediction, envData }) => {
  if (!prediction || !prediction.predictions) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Risk Drivers (composition)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            No prediction data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const timeframe: "5y" | "10y" =
    (prediction.predictions.timeframe as "5y" | "10y") || "5y";

  const getPredValue = (
    metric: "deforestation" | "desertification" | "species" | "landLoss",
  ) => {
    const p = prediction.predictions as any;
    switch (metric) {
      case "deforestation":
        return timeframe === "10y"
          ? (p.deforestation_rate_10y ?? p.deforestation_rate_5y)
          : p.deforestation_rate_5y;
      case "desertification":
        return timeframe === "10y"
          ? (p.desertification_area_10y ?? p.desertification_area_5y)
          : p.desertification_area_5y;
      case "species":
        return timeframe === "10y"
          ? (p.species_impact_10y ?? p.species_impact_5y)
          : p.species_impact_5y;
      case "landLoss":
        return timeframe === "10y"
          ? (p.land_loss_10y ?? p.land_loss_5y)
          : p.land_loss_5y;
      default:
        return 0;
    }
  };

  const current = {
    deforestation: envData?.deforestation?.ratePerYear ?? 0, // percent
    desertification:
      envData?.desertification?.affectedArea ??
      envData?.desertification?.ratePerYear ??
      0, // km² preferred
    landLoss:
      envData?.landLoss?.totalLost ?? envData?.landLoss?.ratePerYear ?? 0, // km² preferred
    species: envData?.endangeredSpecies?.count ?? 0, // count
  };

  const predicted = {
    deforestation: getPredValue("deforestation") ?? 0,
    desertification: getPredValue("desertification") ?? 0,
    landLoss: getPredValue("landLoss") ?? 0,
    // convert predicted species impact percent to estimated count impacted
    species:
      (envData?.endangeredSpecies?.count ?? 0) *
      ((getPredValue("species") ?? 0) / 100),
  };

  // Percent change per metric (unitless) - normalized using max(abs(current), 1) to avoid division by small numbers
  const changes = [
    {
      key: "Land Loss",
      metric: "landLoss",
      current: current.landLoss,
      predicted: predicted.landLoss,
    },
    {
      key: "Deforestation",
      metric: "deforestation",
      current: current.deforestation,
      predicted: predicted.deforestation,
    },
    {
      key: "Desertification",
      metric: "desertification",
      current: current.desertification,
      predicted: predicted.desertification,
    },
    {
      key: "Species loss",
      metric: "species",
      current: current.species,
      predicted: predicted.species,
    },
  ];

  const computed = changes.map((c) => {
    const denom = Math.max(Math.abs(c.current), 1);
    const rawChange = c.predicted - c.current;
    const pctChange = (rawChange / denom) * 100; // percent change
    return {
      key: c.key,
      metric: c.metric,
      current: c.current,
      predicted: c.predicted,
      rawChange,
      pctChange,
      magnitude: Math.abs(pctChange),
      direction:
        pctChange > 0 ? "increase" : pctChange < 0 ? "decrease" : "stable",
    };
  });

  const totalMagnitude = computed.reduce((s, x) => s + x.magnitude, 0) || 1;

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle>Risk Drivers (projected change)</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Column Headers */}
        <div className="grid grid-cols-3 gap-4 pb-2 mb-4 border-b border-gray-200 dark:border-gray-700 text-left">
          <div className="text-xs font-semibold text-muted-foreground">
            Driver
          </div>
          <div className="text-xs font-semibold text-muted-foreground text-center">
            Impact Magnitude
          </div>
          <div className="text-xs font-semibold text-muted-foreground text-right">
            Change
          </div>
        </div>

        <div className="space-y-3 text-left">
          {computed.map((d) => {
            const pctOfDrivers = (d.magnitude / totalMagnitude) * 100;
            const barColor =
              d.direction === "increase"
                ? "from-rose-400 to-rose-600"
                : "from-emerald-400 to-emerald-600";
            return (
              <div key={d.key} className="grid grid-cols-3 gap-4 items-center">
                <div className="text-sm">{d.key}</div>
                <div className="h-3 bg-muted rounded overflow-hidden">
                  <div
                    className={`h-3 bg-gradient-to-r ${barColor}`}
                    style={{ width: `${pctOfDrivers}%` }}
                  />
                </div>
                <div className="text-right text-sm">
                  <span
                    className={`font-medium ${d.direction === "increase" ? "text-rose-600" : d.direction === "decrease" ? "text-emerald-600" : "text-muted-foreground"}`}
                  >
                    {d.direction === "increase"
                      ? "+"
                      : d.direction === "decrease"
                        ? "−"
                        : ""}
                    {Math.abs(d.pctChange).toFixed(1)}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-12 text-xs text-muted-foreground">
          Drivers are shown by projected percent-change magnitude (unitless)
          normalized across metrics. Positive values indicate an increase;
          negative values indicate a decrease. This avoids mixing raw units
          (counts vs percentages) which can be misleading.
        </div>
      </CardContent>
    </Card>
  );
};

export default PredictionDrivers;
